import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

export type StaffToastVariant = 'error' | 'success';

export type StaffToastPayload = {
  variant: StaffToastVariant;
  message: string;
};

type StaffToastContextValue = (payload: StaffToastPayload) => void;

const StaffToastContext = createContext<StaffToastContextValue | null>(null);

type ToastItem = StaffToastPayload & { id: number };

const TOAST_TTL_SUCCESS = 5000;
const TOAST_TTL_ERROR = 30000;

/** Стабильная ссылка вне `StaffToastProvider` (иначе хуки с `[toast]` в deps зацикливают эффекты). */
const staffToastNoop: StaffToastContextValue = () => {};

export function StaffToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const itemsRef = useRef<ToastItem[]>([]);
  const idRef = useRef(0);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const mountedRef = useRef(true);
  const regionId = useId();

  const remove = useCallback((id: number) => {
    timersRef.current.delete(id);
    itemsRef.current = itemsRef.current.filter((t) => t.id !== id);
    if (!mountedRef.current) return;
    setItems(itemsRef.current);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const timers = timersRef.current;
    return () => {
      mountedRef.current = false;
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
      itemsRef.current = [];
    };
  }, []);

  const show = useCallback(
    (payload: StaffToastPayload) => {
      const ttl = payload.variant === 'error' ? TOAST_TTL_ERROR : TOAST_TTL_SUCCESS;
      // Одинаковые тексты не плодим: заменяем существующий тост и продлеваем его TTL.
      const existing = itemsRef.current.find((t) => t.message === payload.message);
      if (existing) {
        const prevTimer = timersRef.current.get(existing.id);
        if (prevTimer) clearTimeout(prevTimer);
        timersRef.current.set(
          existing.id,
          setTimeout(() => remove(existing.id), ttl)
        );
        return;
      }
      const id = ++idRef.current;
      const next: ToastItem = { id, ...payload };
      itemsRef.current = [...itemsRef.current, next];
      setItems(itemsRef.current);
      timersRef.current.set(
        id,
        setTimeout(() => remove(id), ttl)
      );
    },
    [remove]
  );

  const value = useMemo(() => show, [show]);

  return (
    <StaffToastContext.Provider value={value}>
      {children}
      <div
        id={regionId}
        aria-live="polite"
        aria-relevant="additions"
        className="pointer-events-none fixed bottom-4 right-4 z-[50000] flex max-w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
      >
        {items.map((t) => (
          <div
            key={t.id}
            role="status"
            className={cn(
              'pointer-events-auto rounded-sm border px-3 py-2 text-sm shadow-md',
              t.variant === 'error'
                ? 'border-amber-800/30 bg-amber-950/90 text-amber-50 dark:border-amber-700/40 dark:bg-amber-950/95 dark:text-amber-100'
                : 'border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] text-[hsl(var(--ui-card-foreground))]'
            )}
          >
            {t.message}
          </div>
        ))}
      </div>
    </StaffToastContext.Provider>
  );
}

export function useStaffToast(): StaffToastContextValue {
  const ctx = useContext(StaffToastContext);
  return ctx ?? staffToastNoop;
}
